import {
    getVoucherStatusColor,
    Voucher,
    getVoucherPurchasedBy,
    getVoucherRedeemedBy,
    getVoucherPurchasedOn,
    getVoucherRedeemedOn,
    getVoucherSalesInterface,
    getVoucherPurchaseTransaction,
} from '../../model/voucher.types';
import { StajackTableColumn } from '@starjack/shared-components';
import { Chip, Stack, SvgIcon } from '@mui/material';
import { formatDateToString } from '../voucherUiUtils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useVoucherLabelTranslations } from './useVoucherLabelTranslations';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@heroicons/react/24/solid/ArrowDownTrayIcon';

export const voucherTableColumnIds = [
    'code',
    'purchasedBy',
    'redeemedBy',
    'purchasedOn',
    'redeemedOn',
    'salesInterface',
    'status',
    'action',
] as const;

export type VoucherTableColumnId = (typeof voucherTableColumnIds)[number];

type VoucherTableColumn = {
    columnId: VoucherTableColumnId;
} & StajackTableColumn<Voucher>;

export const useVoucherColumns = (
    onDownload?: (voucher: Voucher) => Promise<void>
): VoucherTableColumn[] => {
    const { t } = useTranslation();
    const { getVoucherStatusAsString } = useVoucherLabelTranslations();

    return useMemo(
        () => [
            {
                label: t('voucherColumns.code'),
                columnId: 'code',
                itemTransform: (voucher) => {
                    const code = voucher.code;
                    if (!code || code.trim() === '') {
                        return '-';
                    }
                    return code;
                },
            },

            {
                label: t('voucherColumns.purchasedBy'),
                columnId: 'purchasedBy',
                itemTransform: (voucher) =>
                    getVoucherPurchasedBy(voucher) || '-',
            },

            {
                label: t('voucherColumns.redeemedBy'),
                columnId: 'redeemedBy',
                itemTransform: (voucher) =>
                    getVoucherRedeemedBy(voucher) || '-',
            },

            {
                label: t('voucherColumns.purchasedOn'),
                columnId: 'purchasedOn',
                itemTransform: (voucher) =>
                    formatDateToString(getVoucherPurchasedOn(voucher), {
                        detailed: true,
                    }) || '-',
            },

            {
                label: t('voucherColumns.redeemedOn'),
                columnId: 'redeemedOn',
                itemTransform: (voucher) => {
                    const redeemedOn = getVoucherRedeemedOn(voucher);
                    return redeemedOn
                        ? formatDateToString(redeemedOn, { detailed: true })
                        : '-';
                },
            },

            {
                label: t('voucherColumns.salesInterface'),
                columnId: 'salesInterface',
                itemTransform: (voucher) =>
                    getVoucherSalesInterface(voucher) || '-',
            },

            {
                label: t('voucherColumns.status'),
                itemTransform: (voucher) => (
                    <Stack direction='row' spacing={1}>
                        <Chip
                            color={getVoucherStatusColor(voucher.status)}
                            label={getVoucherStatusAsString(voucher.status)}
                        />
                        {/* Issued without payment (#1904): not a sale. */}
                        {voucher.issuedBy && (
                            <Chip
                                variant='outlined'
                                label={t('voucherColumns.issued')}
                            />
                        )}
                    </Stack>
                ),
                columnId: 'status',
            },
            ...(onDownload
                ? [
                      {
                          label: t('voucherColumns.action'),
                          columnId: 'action' as const,
                          itemTransform: (voucher: Voucher) => {
                              const purchaseTransaction =
                                  getVoucherPurchaseTransaction(voucher);
                              const isPaid = !!purchaseTransaction;

                              if (!isPaid) {
                                  return null;
                              }

                              return (
                                  <IconButton
                                      aria-label='Gutschein herunterladen'
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          onDownload(voucher);
                                      }}
                                      size='small'
                                  >
                                      <SvgIcon>
                                          <DownloadIcon />
                                      </SvgIcon>
                                  </IconButton>
                              );
                          },
                      },
                  ]
                : []),
        ],
        [t, getVoucherStatusAsString, onDownload]
    );
};
